import { Fragment } from "react";
import { CarPortfolioChartItem } from "../asset-allocation-types";
import { UseChartDataSelector } from "./useChartDataSelector";
import {
  CarInfoCell,
  CarInfoHeader,
  CarInfoTable,
} from "@app/components/InfoTable";
import { DecimalOptions, formatPercentFactor, isOddEven } from "@app/utils";
import { BoxProps } from "@mui/material";
import { ExcelDataFormat, ExcelTable } from "@app/hooks/useExcelExport";

interface TableMultiPeriodReturnComparisonProps {
  sx?: BoxProps["sx"];
  data: CarPortfolioChartItem[];
  chartDataSelector: UseChartDataSelector;
}

const labels = ["3 Month", "1 Year", "3 Year", "5 Year", "10 Year"];

export const getMultiPeriodReturnComparisonExcelTable = (params: {
  data: CarPortfolioChartItem[];
  data1Id: string;
  data2Id: string;
}) => {
  const item1 = params.data.find((i) => i.id === params.data1Id);
  const item2 = params.data.find((i) => i.id === params.data2Id);

  if (!item1 || !item2) {
    const table: ExcelTable = {
      name: "",
      columns: [],
      rows: [],
    };
    return table;
  }

  const decimalOptions: DecimalOptions = {
    decimalPlaces: 1,
    forceShowDecimals: true,
  };
  const table: ExcelTable = {
    name: "Multi-Period Return",
    columns: [
      { label: "", format: ExcelDataFormat.general },
      { label: item1.label, format: ExcelDataFormat.percent, decimalOptions },
      { label: item2.label, format: ExcelDataFormat.percent, decimalOptions },
      { label: "Difference", format: ExcelDataFormat.percent, decimalOptions },
    ],
    rows:
      labels.map((label, idx) => [
        label,
        item1.y[idx],
        item2.y[idx],
        item1.y[idx] - item2.y[idx],
      ]) ?? [],
  };

  return table;
};

export const TableMultiPeriodReturnComparison = ({
  sx,
  data,
  chartDataSelector: { data1Id, data2Id },
}: TableMultiPeriodReturnComparisonProps) => {
  const item1 = data.find((i) => i.id === data1Id);
  const item2 = data.find((i) => i.id === data2Id);

  return item1 && item2 ? (
    <CarInfoTable sx={{ gridTemplateColumns: "repeat(4, 1fr)", ...sx }}>
      <CarInfoHeader variant="par02Regular" text="" />
      <CarInfoHeader variant="par02Regular" text={item1.fullLabel} />
      <CarInfoHeader variant="par02Regular" text={item2.fullLabel} />
      <CarInfoHeader variant="par02Regular" text="Difference:" />
      {labels.map((i, idx) => (
        <Fragment key={idx}>
          <CarInfoCell isOdd={isOddEven(idx)} text={i} justifyContent="start" />
          <CarInfoCell
            isOdd={isOddEven(idx)}
            text={formatPercentFactor(item1.y[idx], 1)}
          />
          <CarInfoCell
            isOdd={isOddEven(idx)}
            text={formatPercentFactor(item2.y[idx], 1)}
          />
          <CarInfoCell
            isOdd={isOddEven(idx)}
            text={formatPercentFactor(item1.y[idx] - item2.y[idx], 1)}
          />
        </Fragment>
      ))}
    </CarInfoTable>
  ) : null;
};
